import checkcircle from "@styles/all-images/icons/check-circle-fill.svg";
import closecircle from "@styles/all-images/icons/close-circle.svg";
import clocksolid from "@styles/all-images/icons/clock_solid.svg";
import infocircle from "@styles/all-images/icons/info.svg";
import React, { useState, useEffect } from "react";
import FileTimeLineRejectedReason from "@/pages/T4/T4FilesTimeline/FileTimeLineRejectedReason";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import { toast } from "react-toastify";
import { Document } from "@/pages/T4/DocumentMasterTab";
import {
  DocumentApprovalStatus,
  DocumentApprovalStatusText,
  getDocumentApprovalStatusStyle,
} from "@/constants/enums/document-approval-status.enum";
import dateHandler from "@/composables/dateUtil";
import ViewRejectionReason from "@/components/common/ViewRejectedReason";

export interface TimelineData {
  document_history_id: number;
  document_info: {
    document_id: number;
    document_status: string;
    document_note: string | null;
  };
  document_master_info: {
    document_master_id: number;
    document_master_name: string;
  };
  action_by: {
    id: number;
    name: string;
  };
  status: {
    id: number;
    name: string;
  };
  version_id: string;
  s3_key: string;
  file_name: string;
  size: number;
  input_fields: any;
  note: string | null;
  mark_as_na: boolean;
  action_at: string;
}

interface DocumentProps {
  documents?: Document[];
  timelineData?: Record<string, TimelineData[]>;
  skipFetch?: boolean;
}

const ViewTimeLineDocument: React.FC<DocumentProps> = ({
  documents,
  timelineData: initialTimelineData,
  skipFetch = false,
}) => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const [timelineData, setTimelineData] = useState<
    Record<string, TimelineData[]>
  >(initialTimelineData || {});
  const [isLoading, setIsLoading] = useState(false);
  const [isRejectionReasonModalOpen, setIsRejectionReasonModalOpen] =
    useState(false);
  const [selectedRejectionNote, setSelectedRejectionNote] = useState<
    string | null
  >(null);

  const fetchAllTimelineData = async () => {
    if (skipFetch || !documents?.length) return;

    try {
      setIsLoading(true);
      const { authenticatedCall } = actionsHandler("filesTimeline");

      const promises = documents.map((doc) =>
        dispatch(
          authenticatedCall({
            method: "get",
            customParams: [`${doc.uploaded_document.id}/`],
          }),
        ).then((response) => {
          if (doc.uploaded_document.id) {
            return {
              [doc.uploaded_document.id.toString()]: response.data.data,
            };
          }
        }),
      );

      const responses = await Promise.all(promises);
      const timelineByDoc = responses.reduce(
        (acc, response) => ({ ...acc, ...response }),
        {} as Record<string, TimelineData[]>,
      );

      setTimelineData(timelineByDoc || {});
    } catch (error) {
      console.error("Error fetching timeline:", error);
      toast.error("Failed to load timeline data");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (documents?.length && !skipFetch) {
      fetchAllTimelineData();
    }
  }, [documents, skipFetch]);

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return checkcircle;
      case "rejected":
        return closecircle;
      default:
        return clocksolid;
    }
  };

  const formatTimelineData = () => {
    return Object.entries(timelineData).map(([docId, docData]) => ({
      title: docData[0].document_master_info.document_master_name,
      icon: getStatusIcon(docData[0].document_info.document_status),
      details: docData.map((item) => {
        const { bgColor, textColor } = getDocumentApprovalStatusStyle(
          Number(item.status.id || item.status) as DocumentApprovalStatus,
        );
        return {
          time: dateHandler().formatTimelineDate(item.action_at),
          description: `Document ${item.status.name} by ${item.action_by.name}`,
          status: item.document_info.document_status,
          statusText: item.mark_as_na
            ? DocumentApprovalStatusText[DocumentApprovalStatus.mark_as_na]
            : DocumentApprovalStatusText[
                Number(item.status.id || item.status) as DocumentApprovalStatus
              ],
          bgColor,
          textColor,
          action_by: item.action_by.name,
          document_note: item.document_info.document_note,
          showRejectionModal: item.document_info.document_note !== null,
        };
      }),
    }));
  };

  const handleOpenRejectionReasonModal = (note: string | null) => {
    setSelectedRejectionNote(note);
    setIsRejectionReasonModalOpen(true);
  };

  const handleCloseRejectionReasonModal = () => {
    setIsRejectionReasonModalOpen(false);
  };

  const handleRejectionSubmit = (data: any) => {
    console.log("Rejection reason submitted:", data);
    handleCloseRejectionReasonModal();
  };

  return (
    <div className="tw-relative tw-bg-[#F8FAFC]">
      <div className="tw-flex tw-justify-left tw-gap-x-5 tw-gap-y-8 tw-p-10 tw-overflow-x-scroll tw-w-full">
        {isLoading ? (
          <div className="tw-flex tw-justify-center tw-items-center tw-w-full">
            <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-gray-900" />
          </div>
        ) : (
          formatTimelineData().map((document, index) => (
            <div
              key={index}
              className="tw-bg-[#FFFFFF] tw-rounded-lg tw-border tw-pb-5 tw-border-[#E9EAEB] tw-w-[33%]"
            >
              <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-[#E9EAEB] tw-py-4 tw-relative tw-px-4 tw-bg-white">
                <div className="">
                  <h2 className="tw-text-base tw-font-semibold tw-text-[#232323]">
                    {document.title}
                  </h2>
                </div>
                <div className="tw-w-4 tw-h-6 tw-max-w-6 tw-max-h-6 tw-min-w-6 tw-min-h-6">
                  <img src={document.icon} alt={document.title} />
                </div>
              </div>
              <div className="tw-h-[410px] tw-overflow-y-auto">
                {document.details.map((detail, detailIndex) => (
                  <div
                    key={detailIndex}
                    className="tw-flex tw-justify-start tw-items-start tw-gap-x-6 tw-px-4 tw-mt-5"
                  >
                    <div className="tw-relative">
                      <span className="tw-w-5 tw-h-5 tw-rounded-full tw-bg-[#C1C0C0] tw-flex tw-items-center tw-justify-center filestimeline-circle-line"></span>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 tw-border tw-border-[#E9EAEB] tw-rounded-lg tw-p-4 tw-w-full tw-relative">
                      <p className="tw-text-sm te-font-normal tw-text-[#809199] tw-mb-1">
                        {detail.time}
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="tw-text-base tw-font-medium tw-text-[#050708]">
                          {skipFetch ? "Details" : "Document"}
                          {detail.statusText === "Pending" && " Approval"}
                          <span
                            className={`tw-text-sm tw-font-medium tw-py-1 tw-px-1 tw-rounded-md ${detail.bgColor} ${detail.textColor}`}
                          >
                            {detail.statusText}{" "}
                          </span>
                          By{" "}
                          <span className="tw-text-[#050708] tw-font-bold">
                            {detail.action_by}
                          </span>
                        </span>
                      </div>
                      {detail.statusText?.toLowerCase() === "rejected" &&
                        detail.showRejectionModal && (
                          <div className="tw-absolute tw-top-1 tw-right-1">
                            <button
                              onClick={() =>
                                handleOpenRejectionReasonModal(
                                  detail.document_note,
                                )
                              }
                            >
                              <img src={infocircle} alt="info icon" />
                            </button>
                          </div>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
      {isRejectionReasonModalOpen && (
        // <FileTimeLineRejectedReason
        //   onClose={handleCloseRejectionReasonModal}
        //   onSubmit={handleRejectionSubmit}
        //   rejectionData={{
        //     title: "Document Rejection Reason",
        //     comment: selectedRejectionNote || "",
        //   }}
        // />
        <ViewRejectionReason
          onClose={handleCloseRejectionReasonModal}
          rejectionReason={selectedRejectionNote || ""}
        />
      )}
    </div>
  );
};

export default ViewTimeLineDocument;
